import {
  Background,
  type Edge,
  PanOnScrollMode,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useMy, useUploadFile } from '@/hooks';
import { CustomBezierEdge } from '@/sections/attacks/attackPaths/components/edges';
import { nodeTypes } from '@/sections/attacks/attackPaths/config';
import {
  markerColors,
  techniqueColors,
} from '@/sections/attacks/attackPaths/constants';
import {
  AttackContext,
  type AttackContextType,
} from '@/sections/attacks/attackPaths/context';
import { getAttackPathIdFromName } from '@/sections/attacks/attackPaths/utils';

const edgeTypes = { bezier: CustomBezierEdge };

const noop = () => {};

// Minimal stub context for read-only rendering — nodes only read isEditMode and removeNode
const readOnlyContext: AttackContextType = {
  attackPaths: [],
  name: '',
  nodes: [],
  edges: [],
  stages: [],
  hosts: [],
  engagement: {},
  isEditMode: false,
  selectedAttackPath: '',
  activeView: 'graph',
  focusedStageIndex: null,
  compactMode: false,
  setName: noop,
  setNodes: noop,
  setEdges: noop,
  setStages: noop,
  setHosts: noop,
  setEngagement: noop,
  setIsEditMode: noop,
  setSelectedAttackPath: noop,
  setActiveView: noop,
  setFocusedStageIndex: noop,
  addNode: noop,
  removeNode: noop,
  updateNode: noop,
  addEdge: noop,
  removeEdge: noop,
  onNodesChange: noop,
  onEdgesChange: noop,
  isFetchingAttackPaths: false,
  isFetchingAttackPath: false,
  onEdit: noop,
  onCancel: noop,
  onSave: noop,
  onDelete: noop,
  onCreate: noop,
  onChange: noop,
  recenter: noop,
};

interface ChatAttackGraphProps {
  name: string;
  graph: {
    nodes: Array<{
      id: string;
      type: string;
      data: Record<string, unknown>;
      position?: { x: number; y: number };
    }>;
    edges: Array<{ id: string; source: string; target: string }>;
    stages?: unknown[];
  };
}

function AttackGraphInner({ name: defaultName, graph }: ChatAttackGraphProps) {
  const { mutateAsync: uploadFile } = useUploadFile();
  const [saveName, setSaveName] = useState(defaultName);
  const [saved, setSaved] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const { data: existingFiles } = useMy({
    resource: 'file',
    query: `#${getAttackPathIdFromName('')}`,
  });

  const existingNames = useMemo(
    () => new Set(existingFiles.map(f => f.name.replace('attack-path-', ''))),
    [existingFiles]
  );

  const willOverwrite = existingNames.has(saveName);

  const doSave = useCallback(async () => {
    try {
      await uploadFile({
        name: getAttackPathIdFromName(saveName),
        content: JSON.stringify(graph),
      });
      setSaved(true);
      setConfirmOverwrite(false);
      toast.success('Saved to Attack Paths');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save attack path');
    }
  }, [uploadFile, saveName, graph]);

  const handleSave = useCallback(() => {
    if (!saveName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    if (willOverwrite && !confirmOverwrite) {
      setConfirmOverwrite(true);
      return;
    }
    void doSave();
  }, [saveName, willOverwrite, confirmOverwrite, doSave]);

  const coloredEdges: Edge[] = graph.edges.map(edge => {
    const sourceNode = graph.nodes.find(n => n.id === edge.source);
    const targetNode = graph.nodes.find(n => n.id === edge.target);
    const defaultHex = { light: '#6B7280', dark: '#9CA3AF' };

    const techNode =
      sourceNode?.type === 'technique'
        ? sourceNode
        : targetNode?.type === 'technique'
          ? targetNode
          : null;

    if (techNode) {
      const status = (techNode.data.status as string) || '';
      const color = techniqueColors[status];
      return {
        ...edge,
        type: 'bezier',
        className: color?.line,
        markerEnd: `arrow-${status}`,
        data: {
          strokeHex: color?.lineHex || defaultHex,
          markerHex: color?.markerHex || defaultHex,
          status,
        },
      } as Edge;
    }

    return {
      ...edge,
      type: 'bezier',
      markerEnd: 'arrow-',
      data: { strokeHex: defaultHex, markerHex: defaultHex, status: '' },
    } as Edge;
  });

  return (
    <div className="my-2">
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <div className="relative h-[350px]">
          <AttackContext.Provider
            value={{
              ...readOnlyContext,
              nodes: graph.nodes as never[],
              edges: coloredEdges,
            }}
          >
            <ReactFlow
              nodes={graph.nodes as never[]}
              edges={coloredEdges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={{ type: 'bezier' }}
              fitView
              fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
              panOnScroll
              panOnScrollMode={PanOnScrollMode.Vertical}
              preventScrolling={false}
              nodesConnectable={false}
              nodesDraggable={false}
              elementsSelectable={false}
              maxZoom={1.5}
              minZoom={0.3}
              className="size-full bg-transparent"
            >
              {/* Arrow markers */}
              <div className="absolute top-0 left-0">
                {Object.entries(markerColors).map(([key, colors]) => (
                  <svg
                    key={key}
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  >
                    <defs>
                      <marker
                        id={`arrow-${key}`}
                        viewBox="0 0 10 10"
                        markerWidth={12}
                        markerHeight={8}
                        refX={8}
                        refY={5}
                        orient="auto"
                        markerUnits="strokeWidth"
                        className={colors.marker}
                      >
                        <path
                          d="M 0 0 L 10 5 L 0 10 z"
                          className="fill-current"
                        />
                      </marker>
                    </defs>
                  </svg>
                ))}
              </div>
              <Background color="rgb(var(--chariot-stroke) / 0.3)" size={4} />
            </ReactFlow>
          </AttackContext.Provider>
        </div>

        {/* Action bar */}
        <div className="border-chariot-stroke border-t px-3 py-2">
          {saved ? (
            <div className="flex items-center justify-between">
              <span className="text-chariot-text-tertiary text-xs">
                {graph.nodes.length} nodes · {graph.edges.length} edges
              </span>
              <span className="flex items-center gap-1 text-xs text-green-400">
                <span>✓</span> Saved as &quot;{saveName}&quot;
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={saveName}
                  onChange={e => {
                    setSaveName(e.target.value);
                    setConfirmOverwrite(false);
                  }}
                  placeholder="Attack path name"
                  className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary min-w-0 flex-1 rounded border px-2 py-1 text-xs"
                />
                <button
                  onClick={handleSave}
                  className="text-chariot-brand-primary hover:bg-chariot-brand-primary/10 flex flex-shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors hover:cursor-pointer"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  {confirmOverwrite
                    ? 'Confirm Overwrite'
                    : 'Save to Attack Paths'}
                </button>
              </div>
              {confirmOverwrite && (
                <div className="flex items-center gap-2 text-xs text-yellow-500">
                  <span>
                    An attack path named &quot;{saveName}&quot; already exists.
                    Click again to overwrite.
                  </span>
                  <button
                    onClick={() => setConfirmOverwrite(false)}
                    className="text-chariot-text-tertiary hover:text-chariot-text-secondary underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ChatAttackGraph: React.FC<ChatAttackGraphProps> = props => (
  <ReactFlowProvider>
    <AttackGraphInner {...props} />
  </ReactFlowProvider>
);

export default ChatAttackGraph;
